import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  isEmpty,
  isNil,
} from 'ramda';
import { withStyles } from '@material-ui/core/styles';
import { withSnackbar } from 'notistack';
import LinearProgress from '@material-ui/core/LinearProgress';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import IconButton from '@material-ui/core/IconButton';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { AlertHelpDialog } from '@iris-platform/iris-react-ui';
import { withApollo } from 'react-apollo';
import {
  stylesToPropType,
  handleChange,
  isUrl,
} from '../../../utils';
import { GET_SSO, UPDATE_SSO } from '../queries';
import Helpfile from '../helpfile.json';

const styles = () => ({
  root: {
    width: '100%',
  },
  saveButton: {
    borderRadius: '0.125em',
    marginTop: '1em',
    width: '5em',
  },
  icon: {
    // margin: theme.spacing.unit,
    fontSize: 14,
    cursor: 'pointer',
  },
  iconbutton: {
    width: '18px',
    height: '18px',
  },
});

class Sso extends Component {
  constructor(props) {
    super(props);

    this.state = {
      sso: {},
      pendingChanges: false,
      error: null,
      serverUrlError: null,
      loading: true,
      open: false,
      dialogTitle: '',
      dialogContent: '',
    };
  }

  async componentDidMount() {
    const { appKey, client } = this.props;

    this.setState({ loading: true });

    try {
      const { data: { sso } } = await client.query({
        query: GET_SSO,
        variables: { appKey },
      });
      this.setState({
        sso: {
          app_key: appKey,
          server_url: sso.server_url,
        },
        loading: false,
      });
    } catch (error) {
      this.setState({ error });
    } finally {
      this.setState({ loading: false });
    }
  }

  handleHelpOpen = (title, content) => {
    this.setState({
      open: true,
      dialogTitle: title,
      dialogContent: content,
    });
  };

  handleHelpClose = () => {
    this.setState({
      open: false,
      dialogTitle: '',
      dialogContent: '',
    });
  };

  saveChanges = async () => {
    const { client, enqueueSnackbar } = this.props;
    const {
      sso,
      pendingChanges,
    } = this.state;

    const isServerUrlInvalid = (
      isNil(sso.server_url)
      || isEmpty(sso.server_url)
      || !isUrl(sso.server_url)
    );

    if (isServerUrlInvalid) {
      enqueueSnackbar('Failed to update SSO auth settings', { variant: 'error' });
      this.setState({
        serverUrlError: isServerUrlInvalid ? new Error('Server URL must be a valid URL') : null,
      });
    } else if (
      pendingChanges
    ) {
      this.setState({ loading: true });

      try {
        await client.mutate({
          mutation: UPDATE_SSO,
          variables: { sso },
        });
        enqueueSnackbar('SSO auth settings updated', { variant: 'success' });
        this.setState({ loading: false, pendingChanges: false, error: null });
      } catch (error) {
        enqueueSnackbar('Failed to update SSO auth settings', { variant: 'error' });
        this.setState({
          error: new Error('Unable to save changes, please try again'),
        });
      } finally {
        this.setState({ loading: false });
      }
    }
  }

  render() {
    const { classes } = this.props;
    const {
      sso: {
        server_url: serverUrl,
      },
      pendingChanges,
      error,
      serverUrlError,
      loading,
      open,
      dialogTitle,
      dialogContent,
    } = this.state;
    const help = Helpfile.auth;

    return (
      <Fragment>
        <Typography
          variant="subheading"
          gutterBottom
        >
          SSO Auth Settings
          <IconButton className={classes.iconbutton} aria-label="Delete" onClick={() => this.handleHelpOpen(help.ssoAuth.title, help.ssoAuth.content)}>
            <HelpOutlineIcon className={classes.icon} />
          </IconButton>
        </Typography>
        {loading && <LinearProgress />}
        <form>
          <FormControl className={classes.root} error={!!serverUrlError || !!error}>
            <TextField
              label="Server Authentication"
              value={serverUrl || ''}
              onChange={(event) => {
                handleChange(this, ['sso', 'server_url'])(event);
                this.setState({
                  serverUrlError: null,
                  pendingChanges: true,
                });
              }}
              type="text"
              placeholder="SSO Token Verification Server Url (https://websec-stg.cable.comcast.com/idp/userinfo.openid?access_token=)"
              InputLabelProps={{ shrink: true }}
              margin="normal"
            />
            {serverUrlError && <FormHelperText>{serverUrlError.message}</FormHelperText>}

            <Button
              variant="contained"
              classes={{ root: classes.saveButton }}
              disabled={!pendingChanges}
              onClick={this.saveChanges}
            >
              Save
            </Button>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        </form>
        <AlertHelpDialog
          handleClose={this.handleHelpClose}
          open={open}
          dialogTitle={dialogTitle}
          dialogContent={dialogContent}
        />
      </Fragment>
    );
  }
}

Sso.propTypes = {
  appKey: PropTypes.string.isRequired,
  classes: stylesToPropType(styles()).isRequired,
  client: PropTypes.object.isRequired,
};

export default withSnackbar(withStyles(styles)(withApollo(Sso)));
